import { type ReactElement, useContext, useMemo } from "react"

import { useTranslation } from "react-i18next"

import CloseIcon from "@mui/icons-material/Close"
import { IconButton } from "@mui/material"
import { MenuItem, Tag, Typography } from "@synapse-analytics/synapse-ui"
import { useFormikContext } from "formik"

import { KonanAvatarComponent } from "../../../components/core/KonanAvatar"
import { Context } from "../../../features/ModelConfigurations/Context"
import { getAutoMLConfigHistoryData } from "../../../features/ModelConfigurations/Helpers"
import type { ConfigFormikProps } from "../../../features/ModelConfigurations/Interfaces"
import { getTheme } from "../../../hooks/UseTheme"
import type { ModelSettingsSet } from "../../../types/generated/api/ModelSettingsSet"
import { isToday } from "../../../utils/genericHelpers"
import { formateDate } from "./Helpers"

import styles from "./ConfigSidebarContainer.module.scss"

/**
 * Component that renders the Model Configurations side bar.
 * It fetches the models and their configurations for the current project and renders
 * a list of configurations grouped by date.
 * If the component is still loading or fetching, it displays skeletons.
 * If an error occurs while fetching the configurations, it displays an error message.
 * @returns {ReactElement} The component element.
 */
export const ConfigSidebarContainer = (): ReactElement => {
  const { t } = useTranslation("ModelConfigurations")

  const {
    configHistoryList,
    latestVersionNum,
    currentConfigHistory,
    thresholdMetricsData,
    setCurrentConfigHistory,
    setCurrentThresholdMetricData,
    setSelectedVersionNum,
    setIsSidebarOpen,
  } = useContext(Context)

  const { setFieldValue } = useFormikContext<ConfigFormikProps>()

  const theme = getTheme()

  // useMemo here because i don't want the version variable to get reassigned on each rerender
  // I want it to get assigned only once on initial rerender.
  let version = useMemo(() => {
    return latestVersionNum
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const groupedConfigsByDates = Object.groupBy(configHistoryList || [], ({ created_at }) => {
    const formattedDate = formateDate(created_at)

    return isToday(created_at) ? "Today" : formattedDate
  })

  /**
   * Handles the click event of a history item.
   * Sets the current configuration to the clicked item and updates the form with the corresponding values.
   * Updates the heatmap data to the confusion matrix data of the clicked item.
   * @param {ModelSettingsSet} item - The configuration item to be set as the current configuration.
   * @param {number} version - The version number of the configuration item.
   */
  const historyItemClickHandler = (item: ModelSettingsSet, version: number): void => {
    setCurrentConfigHistory(item)

    if (thresholdMetricsData?.type === "V2") {
      const { posItem, negItem, currentMetricsData } = getAutoMLConfigHistoryData(
        thresholdMetricsData.thresholdMetrics,
        thresholdMetricsData.type,
        item.settings,
      )

      setFieldValue("positiveLabel", posItem?.label)
      setFieldValue("negativeLabel", negItem?.label)

      setFieldValue("negativeThreshold", negItem?.upper_bound)
      setFieldValue("positiveThreshold", posItem?.lower_bound)

      setCurrentThresholdMetricData(currentMetricsData)
    } else if (thresholdMetricsData) {
      const { posItem, negItem, currentMetricsData } = getAutoMLConfigHistoryData(
        thresholdMetricsData.thresholdMetrics,
        thresholdMetricsData.type,
        item.settings,
      )

      setFieldValue("positiveLabel", posItem?.label)
      setFieldValue("negativeLabel", negItem?.label)

      setFieldValue("singleThreshold", negItem?.upper_bound)

      setCurrentThresholdMetricData(currentMetricsData)
    }

    setSelectedVersionNum(version)
  }

  return (
    <div className={styles.historyContainer}>
      <div className={styles.sideBarHeader}>
        <Typography variant="h3-bold">{t("sidebar.title")}</Typography>
        <IconButton aria-label={t("sidebar.closeButton")} onClick={setIsSidebarOpen.bind(null, (prev) => !prev)}>
          <CloseIcon style={{ color: theme.palette.base.text[2] }} />
        </IconButton>
        {/* <Button
          aria-label="close-history-button"
          onClick={setIsSidebarOpen.bind(null, (prev) => !prev)}
          startIcon={<CloseIcon style={{ color: theme.palette.base.text[2] }} />}
        /> */}
      </div>
      {!groupedConfigsByDates || Object.keys(groupedConfigsByDates).length === 0 ? (
        <Typography variant="p">{t("sidebar.noHistory")}</Typography>
      ) : (
        <div>
          {Object.keys(groupedConfigsByDates).map((date) => (
            <div key={date} className={styles.dateMenuContainer}>
              <Typography variant="p" variantColor={2} className={styles.dateText}>
                {date}
              </Typography>
              <ul>
                {groupedConfigsByDates[date]?.map((item) => {
                  const currentVersion = version && version > 0 ? version-- : 0

                  return (
                    <MenuItem
                      key={item.uuid}
                      isSelected={currentConfigHistory?.uuid === item.uuid}
                      onClick={historyItemClickHandler.bind(null, item, currentVersion)}
                    >
                      <div className={styles.listItem}>
                        <Typography variant="label-bold" noWrap>
                          v{currentVersion.toFixed(1)}
                        </Typography>

                        <div className={styles.avatarContainer}>
                          <KonanAvatarComponent
                            createdBy={item.created_by}
                            title={item.created_by}
                            createdAt={item.created_at}
                          />
                        </div>

                        {item.is_active && (
                          <Tag color="green" size="small" style={{ marginInlineStart: "auto" }}>
                            {t("sidebar.active")}
                          </Tag>
                        )}
                      </div>
                    </MenuItem>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
